import { c as _c } from "react-compiler-runtime";
import { useMemo } from 'react';
import { useResource } from 'pulse/react';
import { AppSettingsEntity } from 'hs-resources/resources/AppSettings';
import { ObjectTypeDefinitions } from 'hs-resources/resources/ObjectTypeDefinition';
import { parse } from 'hub-http/helpers/params';
import { UI_MODE } from 'framework-listing-lib/constants';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useIsGalleryMode, useIsListMode, useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { useListingLibData } from 'framework-listing-lib/internal/providers/DataProvider';
import { LISTING_LIB_APP_SETTING_NAMESPACE } from '../constants';
import { getHasApplicationId, getIsGatedToAll, getIsUngatedToAll, getNotHasApplicationId } from '../utils';
export function useFetchFrameworkAppSettings() {
  "use memo";

  const $ = _c(7);
  const currentObjectTypeId = useObjectTypeId();
  const {
    data: objectTypes,
    error: objectTypesError,
    loading: objectTypesLoading
  } = useResource(ObjectTypeDefinitions);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      params: {
        appSettingName: LISTING_LIB_APP_SETTING_NAMESPACE
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: appSettings,
    error: appSettingsError,
    loading: appSettingsLoading
  } = useResource(AppSettingsEntity, t0);
  let t1;
  bb0: {
    var _objectTypes$find;
    const fullyQualifiedName = objectTypes === null || objectTypes === void 0 || (_objectTypes$find = objectTypes.find(objectTypeDefinition => objectTypeDefinition.objectTypeId === currentObjectTypeId)) === null || _objectTypes$find === void 0 ? void 0 : _objectTypes$find.fullyQualifiedName;
    const appSetting = fullyQualifiedName === undefined ? undefined : appSettings === null || appSettings === void 0 ? void 0 : appSettings[fullyQualifiedName];
    if (!appSetting) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[1] !== appSetting) {
      t2 = {
        [LISTING_LIB_APP_SETTING_NAMESPACE]: appSetting
      };
      $[1] = appSetting;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const appSettingsForObject = t1;
  const t2 = objectTypesError || appSettingsError;
  const t3 = objectTypesLoading || appSettingsLoading;
  let t4;
  if ($[3] !== appSettingsForObject || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      data: appSettingsForObject,
      error: t2,
      loading: t3
    };
    $[3] = appSettingsForObject;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
export function useAppSettingsHaveLoaded() {
  const {
    loading
  } = useFetchFrameworkAppSettings();
  return !loading;
}

/**
 * Read from app settings and only return ui mode options with
 *  - empty gates and scopes OR
 *  - gates array is not empty, scopes array is empty and user has ALL gates OR
 *  - gates array is empty, scopes array is not empty and user has ALL scopes
 */
export function getAppSettingsUIModes({
  isUngatedFor,
  hasScope,
  applicationId,
  appSettings
}) {
  if (!appSettings || !appSettings.uiModes || appSettings.uiModes.value.length === 0) {
    return [UI_MODE.LIST];
  }
  const enabledUIModes = appSettings.uiModes.value.filter(uiModeOption => {
    const isUngatedToAll = getIsUngatedToAll({
      gates: uiModeOption.gates,
      isUngatedFor
    });
    const isGatedToAll = getIsGatedToAll({
      gates: uiModeOption.gates,
      isUngatedFor
    });
    let hasAllScopes = true;
    if (uiModeOption.scopes && uiModeOption.scopes.length) {
      hasAllScopes = uiModeOption.scopes.every(hasScope);
    }
    const hasApplicationId = getHasApplicationId({
      currentApplicationId: applicationId,
      applicationIds: uiModeOption.applicationIds
    });
    const notHasApplicationId = getNotHasApplicationId({
      currentApplicationId: applicationId,
      applicationIds: uiModeOption.applicationIds
    });
    return isUngatedToAll && isGatedToAll && hasAllScopes && hasApplicationId && notHasApplicationId;
  }).map(uiModeOption => uiModeOption.name);
  return [UI_MODE.LIST, ...enabledUIModes];
}
export function useAppSettingsUIModes() {
  "use memo";

  return useListingLibData().uiModes;
}

/**
 * Read from app settings and only return the option when enabled:
 *  - option is present AND
 *  - gates and scopes are empty OR
 *  - gates array is not empty, scopes array is empty and user has ALL gates OR
 *  - gates array is empty, scopes array is not empty and user has ALL scopes
 * @param optionName
 */
export function getHasAppSettingsOption(optionName, context) {
  const {
    isUngatedFor,
    hasScope,
    applicationId,
    appSettings
  } = context;
  if (!appSettings || !appSettings.options) {
    return undefined;
  }
  const appSettingsOption = appSettings.options.value.find(val => val.name === optionName);
  if (!appSettingsOption) {
    return undefined;
  }
  const isUngatedToAll = getIsUngatedToAll({
    gates: appSettingsOption.gates,
    isUngatedFor
  });
  const isGatedToAll = getIsGatedToAll({
    gates: appSettingsOption.gates,
    isUngatedFor
  });
  let hasAllScopes = true;
  if (appSettingsOption.scopes && appSettingsOption.scopes.length) {
    hasAllScopes = appSettingsOption.scopes.every(hasScope);
  }
  const hasApplicationId = getHasApplicationId({
    currentApplicationId: applicationId,
    applicationIds: appSettingsOption.applicationIds
  });
  const notHasApplicationId = getNotHasApplicationId({
    currentApplicationId: applicationId,
    applicationIds: appSettingsOption.applicationIds
  });
  if (isUngatedToAll && isGatedToAll && hasAllScopes && hasApplicationId && notHasApplicationId) {
    return appSettingsOption;
  }
  return undefined;
}
export function useAppSettingsOption(optionName) {
  "use memo";

  const $ = _c(3);
  const {
    getHasAppSettingsOption: memoizedGetHasAppSettingsOption
  } = useListingLibData();
  let t0;
  if ($[0] !== memoizedGetHasAppSettingsOption || $[1] !== optionName) {
    t0 = memoizedGetHasAppSettingsOption(optionName);
    $[0] = memoizedGetHasAppSettingsOption;
    $[1] = optionName;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export function useHasCustomProperties() {
  "use memo";

  const hasCustomPropertiesOption = useAppSettingsOption("hasCustomProperties");
  return Boolean(hasCustomPropertiesOption);
}
export function useHasEditColumns(hasEditColumnsInFoldersMode) {
  "use memo";

  const isListMode = useIsListMode();
  const isFoldersMode = useIsFoldersMode();
  const hasEditColumnsOption = useAppSettingsOption("hasEditColumns");
  return (isListMode || isFoldersMode && hasEditColumnsInFoldersMode) && Boolean(hasEditColumnsOption);
}
export function useHasExport() {
  "use memo";

  const isListMode = useIsListMode();
  const hasExportOption = useAppSettingsOption("hasExport");
  return isListMode && Boolean(hasExportOption);
}
export function useHasNestedFolders() {
  "use memo";

  const hasNestedFolders = useAppSettingsOption("hasNestedFolders");
  return Boolean(hasNestedFolders);
}
export function useHasInlineEdit() {
  "use memo";

  const $ = _c(1);
  const hasInlineEdit = useAppSettingsOption("hasInlineEdit");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parse(window.location.search.substring(1));
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const queryString = t0;
  return Boolean(hasInlineEdit) || queryString.hasInlineEdit === "true";
}
export function useHasCustomizeGallery() {
  "use memo";

  const isGalleryMode = useIsGalleryMode();
  const hasCustomizeGalleryOption = useAppSettingsOption("hasCustomizeGallery");
  return isGalleryMode && Boolean(hasCustomizeGalleryOption);
}
export function useHasBUNavIntegration() {
  "use memo";

  const hasBUNavIntegration = useAppSettingsOption("hasBUNavIntegration");
  return Boolean(hasBUNavIntegration);
}